import { useId, type ReactNode } from "react";

import { cx } from "../lib/cx";

export interface FieldProps {
  label: string;
  description?: string;
  error?: string | undefined;
  /** Receives the id to put on the control it renders. */
  children: (id: string) => ReactNode;
  className?: string;
}

/** Label, optional hint, control, optional error — the one text-field layout. */
export function Field({ label, description, error, children, className }: FieldProps): ReactNode {
  const id = useId();
  return (
    <div className={cx("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {description !== undefined && <p className="text-xs text-muted">{description}</p>}
      {children(id)}
      {error !== undefined && error !== "" && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export const inputClassName =
  "w-full rounded-md border border-line bg-raised px-3 py-2 text-sm disabled:opacity-50";

/** A plain text input wired to a `Field`. */
export function TextField({
  label,
  description,
  error,
  value,
  onChange,
  placeholder,
  disabled,
  type = "text",
}: {
  label: string;
  description?: string;
  error?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  type?: "text" | "email" | "url";
}): ReactNode {
  return (
    <Field
      label={label}
      {...(description === undefined ? {} : { description })}
      {...(error === undefined ? {} : { error })}
    >
      {(id) => (
        <input
          id={id}
          type={type}
          value={value}
          disabled={disabled ?? false}
          {...(placeholder === undefined ? {} : { placeholder })}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className={inputClassName}
        />
      )}
    </Field>
  );
}
